/**
 * Filtering and pricing for the home-page catalog. Kept pure so the route
 * only has to render what these return.
 */

import type { PriceTierRow, Product } from "./api.ts";

export type MailFilter = "addressed" | "unaddressed";
export type CatalogSort = "recommended" | "price-asc" | "price-desc";

export interface CatalogSearch {
  mail?: MailFilter;
  size?: string;
  pages?: number;
  sort?: CatalogSort;
  /** Product to resume the start-campaign modal for, after sign-in. */
  start?: string;
}

const MAILS = new Set<MailFilter>(["addressed", "unaddressed"]);
const SORTS = new Set<CatalogSort>(["recommended", "price-asc", "price-desc"]);

/** Preferred chip order; anything else follows, alphabetically. */
const SIZE_ORDER = ["DL", "MaxPop", "A4", "A5", "A6"];

/** Drops anything that is not a real facet value, so a bad URL shows everything. */
export function parseCatalogSearch(raw: Record<string, unknown>): CatalogSearch {
  const search: CatalogSearch = {};
  if (typeof raw.mail === "string" && MAILS.has(raw.mail as MailFilter)) {
    search.mail = raw.mail as MailFilter;
  }
  if (typeof raw.size === "string" && raw.size !== "") search.size = raw.size;
  const pages = typeof raw.pages === "number" ? raw.pages : Number(raw.pages);
  if (Number.isInteger(pages) && pages > 0) search.pages = pages;
  if (typeof raw.sort === "string" && SORTS.has(raw.sort as CatalogSort)) {
    search.sort = raw.sort as CatalogSort;
  }
  if (typeof raw.start === "string" && raw.start !== "") search.start = raw.start;
  return search;
}

export const hasActiveFilters = (s: CatalogSearch): boolean =>
  Boolean(s.mail || s.size || s.pages || (s.sort && s.sort !== "recommended"));

/** The price shown as "from $x each": the smallest-quantity tier, as before. */
export function basePriceCents(tiers: PriceTierRow[], productId: string): number {
  let best: PriceTierRow | undefined;
  for (const t of tiers) {
    if (t.product_id !== productId) continue;
    if (!best || t.min_qty < best.min_qty) best = t;
  }
  return best?.unit_cents ?? 0;
}

export function facetValues(products: Product[]): { sizes: string[]; pages: number[] } {
  const sizes = [...new Set(products.map((p) => p.size).filter((s): s is string => Boolean(s)))];
  sizes.sort((a, b) => {
    const ia = SIZE_ORDER.indexOf(a);
    const ib = SIZE_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  return { sizes, pages: [...new Set(products.map((p) => p.pages))].sort((a, b) => a - b) };
}

/** Filters combine with AND. Price sorts fall back to sort_order, so ties stay put. */
export function filterProducts(
  products: Product[],
  tiers: PriceTierRow[],
  search: CatalogSearch,
): Product[] {
  const matched = products.filter((p) => {
    if (search.mail === "addressed" && p.addressed_mail !== 1) return false;
    if (search.mail === "unaddressed" && p.unaddressed_mail !== 1) return false;
    if (search.size && p.size !== search.size) return false;
    if (search.pages && p.pages !== search.pages) return false;
    return true;
  });
  const byOrder = (a: Product, b: Product) => a.sort_order - b.sort_order;
  if (search.sort === "price-asc" || search.sort === "price-desc") {
    const dir = search.sort === "price-asc" ? 1 : -1;
    return [...matched].sort(
      (a, b) => dir * (basePriceCents(tiers, a.id) - basePriceCents(tiers, b.id)) || byOrder(a, b),
    );
  }
  return [...matched].sort(byOrder);
}

/** Half-cent precision: 0.255 dollars stays 25.5 cents. */
export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 200) / 2;
}

/** Dollar text for a unit price. Half cents keep three places; whole cents stay at two. */
export function formatUnitDollars(cents: number): string {
  const thousandths = Math.round(cents * 10);
  const third = Math.abs(thousandths) % 10;
  return (thousandths / 1000).toFixed(third === 0 ? 2 : 3);
}

/** Quantity where this break ends: one below the next higher break, or open-ended. */
export function tierRangeEnd(minQty: number, quantities: number[]): string {
  const higher = quantities.filter((q) => q > minQty);
  if (higher.length === 0) return "and above";
  return (Math.min(...higher) - 1).toLocaleString("en-AU");
}
